<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[10050] select-none bg-black text-white">
      <!-- Keeps controls and the photo clear of notch, rounded corners and home indicator. -->
      <div
        class="absolute bottom-[env(safe-area-inset-bottom)] left-[env(safe-area-inset-left)] right-[env(safe-area-inset-right)] top-[env(safe-area-inset-top)]"
      >
        <div
          ref="viewportRef"
          class="absolute flex items-center justify-center portrait:inset-x-0 portrait:bottom-[7rem] portrait:top-0 landscape:inset-y-0 landscape:left-0 landscape:right-[7rem]"
        >
          <div
            class="relative touch-none overflow-hidden rounded-lg"
            :style="{ width: `${stageSize.width}px`, height: `${stageSize.height}px` }"
            @pointerdown="handleSwipeStart"
          >
            <div class="absolute inset-0" :class="slideTransitionClass" :style="getSlideStyle(0)">
              <video
                v-show="!displayedImageUrl"
                ref="videoRef"
                class="absolute inset-0 h-full w-full object-contain"
                autoplay
                playsinline
                muted
                @loadedmetadata="handleVideoMetadata"
              />
            </div>

            <!-- Each shot brings its own photo and overlay, so both slide in together. -->
            <div
              v-for="slide in slides"
              :key="slide.key"
              class="absolute inset-0 overflow-hidden rounded-lg"
              :class="[
                slideTransitionClass,
                { 'bg-obsidian-900': !slide.isCurrent && !slide.imageUrl },
              ]"
              :style="getSlideStyle(slide.offset)"
            >
              <PinchZoomImage
                v-if="slide.imageUrl"
                contain
                :src="slide.imageUrl"
                :scale="slide.isCurrent ? imageScale : 1"
                @update:scale="handleImageScale(slide.isCurrent, $event)"
              >
                <InspectionShotOverlay
                  :shot-key="slide.key"
                  frozen
                  :style="slide.isCurrent ? { opacity: overlayOpacity } : undefined"
                />
              </PinchZoomImage>
              <InspectionShotOverlay v-else :shot-key="slide.key" />

              <template v-if="slide.isCurrent && !slide.imageUrl">
                <div
                  v-if="!isLandscape"
                  class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 p-4 text-center"
                >
                  <SmartphoneIcon class="rotate-hint size-[2.5rem] text-hazel-300" />
                  <p class="font-semibold">{{ t('rotateTitle') }}</p>
                  <p class="max-w-xs text-sm text-white/70">{{ t('rotateText') }}</p>
                </div>
                <div
                  v-else-if="isCameraUnavailable"
                  class="absolute inset-0 flex items-center justify-center bg-black/70 p-8 text-center"
                >
                  <div class="max-w-sm">
                    <CameraOffIcon class="mx-auto size-[2.5rem] text-hazel-300" />
                    <p class="mt-3 font-semibold">{{ t('cameraUnavailableTitle') }}</p>
                    <p class="mt-1 text-sm text-white/70">{{ t('cameraUnavailableText') }}</p>
                  </div>
                </div>
              </template>
            </div>

            <div class="pointer-events-none absolute inset-x-0 top-0 px-[7%] pt-[5%]">
              <div class="relative flex gap-1">
                <button
                  v-for="shot in shots"
                  :key="shot.key"
                  type="button"
                  :class="cameraProgressSegment({ state: shot.state })"
                  :aria-label="getShotLabel(shot.key, fuelType)"
                  @click="handleJump(shot.key)"
                />
                <div
                  class="absolute inset-y-0 rounded-full bg-white"
                  :class="{ 'transition-[left] duration-300 ease-out': isSlideAnimated }"
                  :style="indicatorStyle"
                />
              </div>
              <TransitionGroup
                tag="div"
                class="mt-3 grid"
                :name="isSnapping ? 'hud-title-none' : `hud-title-${titleDirection}`"
                :style="{ '--title-travel': `${stageSize.width * TITLE_PARALLAX}px` }"
              >
                <div
                  v-for="title in titles"
                  :key="title.key"
                  class="hud-title drop-shadow [grid-area:1/1]"
                  :class="{
                    'transition-[transform,opacity] duration-300 ease-out': isSlideAnimated,
                  }"
                  :style="title.style"
                >
                  <p class="text-lg font-semibold">
                    {{ getShotLabel(title.key, fuelType) }}
                    <span class="ml-1 text-sm font-normal text-white/60">
                      {{ title.index + 1 }}/{{ shots.length }}
                    </span>
                  </p>
                  <p class="max-w-md text-sm text-white/80">{{ title.text }}</p>
                </div>
              </TransitionGroup>
            </div>

            <div
              class="pointer-events-none absolute bottom-[8%] left-[6%] w-[3rem] sm:w-[3.5rem]"
              :style="{ opacity: 0.8 * overlayOpacity }"
            >
              <InspectionShotLocator v-if="currentKey" :shot-key="currentKey" />
            </div>

            <p
              v-if="errorMessage"
              class="bg-red-600/90 absolute bottom-4 left-1/2 max-w-[70%] -translate-x-1/2 rounded-md px-4 py-2 text-center text-sm"
            >
              {{ errorMessage }}
            </p>
            <p
              v-else-if="isFrozenBlurred"
              class="pointer-events-none absolute bottom-4 left-1/2 flex max-w-[70%] -translate-x-1/2 items-center gap-2 rounded-md bg-black/70 px-4 py-2 text-sm"
            >
              <AlertTriangleIcon class="size-4 shrink-0 text-hazel-300" />
              {{ t('blurHint') }}
            </p>
          </div>
        </div>

        <div
          class="absolute grid place-items-center bg-black/60 portrait:inset-x-0 portrait:bottom-0 portrait:h-[7rem] portrait:grid-cols-[1fr_auto_1fr] portrait:px-6 landscape:inset-y-0 landscape:right-0 landscape:w-[7rem] landscape:grid-rows-[1fr_auto_1fr] landscape:py-6"
        >
          <!-- Equal outer tracks keep the main button centred however many side buttons show. -->
          <button
            type="button"
            class="portrait:justify-self-start landscape:self-start"
            :class="cameraButton()"
            :aria-label="t('close')"
            @click="handleClose"
          >
            <XIcon class="size-6" />
          </button>

          <!-- The main button stays in place; the small retake button slides out beside it. -->
          <div class="relative">
            <button
              type="button"
              class="transition-opacity duration-300"
              :class="cameraButton({ variant: 'shutter' })"
              :aria-label="t(MAIN_BUTTON_LABELS[mainButtonMode])"
              :disabled="isMainButtonDisabled"
              @click="handleMainButton"
            >
              <span
                class="absolute inset-[6px] rounded-full bg-white transition-transform duration-300 ease-out"
                :class="{
                  'scale-0': mainButtonMode === 'retake',
                  'scale-[1.2]': mainButtonMode === 'confirm',
                }"
              />
              <CameraIcon
                class="absolute inset-0 m-auto size-[2rem] transition-[transform,opacity] duration-300 ease-out"
                :class="
                  mainButtonMode === 'retake' ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                "
              />
              <Loader2Icon
                v-if="isBusy && mainButtonMode === 'confirm'"
                class="absolute inset-0 m-auto size-[2.25rem] animate-spin text-black"
              />
              <CheckIcon
                v-else
                class="absolute inset-0 m-auto size-[2.5rem] text-black transition-[transform,opacity] duration-300 ease-out"
                :class="
                  mainButtonMode === 'confirm' ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                "
              />
            </button>
            <Transition
              enter-active-class="transition duration-300 ease-out"
              leave-active-class="transition duration-200 ease-in"
              enter-from-class="scale-50 opacity-0 portrait:-translate-x-[4.5rem] landscape:-translate-y-[4.5rem]"
              leave-to-class="scale-50 opacity-0 portrait:-translate-x-[4.5rem] landscape:-translate-y-[4.5rem]"
            >
              <button
                v-if="frozenUrl"
                type="button"
                class="absolute portrait:left-full portrait:top-1/2 portrait:-mt-[1.25rem] portrait:ml-5 landscape:left-1/2 landscape:top-full landscape:-ml-[1.25rem] landscape:mt-5"
                :class="cameraButton()"
                :aria-label="t('retake')"
                :disabled="isBusy"
                @click="handleRetake"
              >
                <RotateCcwIcon class="size-6" />
              </button>
            </Transition>
          </div>

          <div
            class="flex items-center gap-3 portrait:justify-self-end landscape:flex-col landscape:self-end"
          >
            <Transition
              enter-active-class="transition duration-300 ease-out"
              leave-active-class="transition duration-200 ease-in"
              enter-from-class="scale-50 opacity-0"
              leave-to-class="scale-50 opacity-0"
            >
              <button
                v-if="hasTorch && isLandscape && !displayedImageUrl"
                type="button"
                :class="cameraButton()"
                :aria-label="t('flash')"
                @click="handleToggleTorch"
              >
                <ZapIcon class="size-6" :class="{ 'text-yellow-400': isTorchOn }" />
              </button>
            </Transition>
            <button
              type="button"
              :class="cameraButton({ variant: 'caption' })"
              :disabled="isBusy"
              @click="handleOpenSkip"
            >
              <SkipForwardIcon class="size-5" />
              {{ t('skip') }}
            </button>
          </div>
        </div>

        <div
          v-if="isSkipOpen"
          class="absolute inset-0 z-20 flex items-center justify-center bg-black/70 p-4"
        >
          <form
            class="w-full max-w-md rounded-xl bg-white p-5 text-gray-900 shadow-xl"
            @submit.prevent="handleSkip"
          >
            <h3 class="text-lg font-semibold">
              {{ t('skipTitle', { shot: currentKey ? getShotLabel(currentKey, fuelType) : '' }) }}
            </h3>
            <p class="mt-1 text-sm text-gray-600">{{ t('skipText') }}</p>
            <Textarea
              v-model:input-text="skipReason"
              class="mt-3"
              :rows="3"
              :placeholder="t('skipPlaceholder')"
              :hint="t('skipHint', { count: trimmedReasonLength, min: MIN_SKIP_REASON_LENGTH })"
            />
            <div class="mt-4 flex justify-end gap-2">
              <CVButton
                type="button"
                variant="ghost"
                :disabled="isBusy"
                @click="isSkipOpen = false"
              >
                {{ t('cancel') }}
              </CVButton>
              <CVButton
                type="submit"
                variant="default"
                :is-loading="isBusy"
                :disabled="trimmedReasonLength < MIN_SKIP_REASON_LENGTH"
              >
                {{ t('skipConfirm') }}
              </CVButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { InspectionShotKey } from '@carvia/ros-client-types';
import { computed, onUnmounted, ref, watch } from 'vue';
import { cameraButton, cameraProgressSegment } from '@carvia/theme/lib/cva/camera-button';
import { useI18n } from 'vue-i18n';
import { useElementSize, useEventListener, useMediaQuery } from '@vueuse/core';
import {
  CameraIcon,
  CameraOffIcon,
  AlertTriangleIcon,
  CheckIcon,
  Loader2Icon,
  RotateCcwIcon,
  SkipForwardIcon,
  SmartphoneIcon,
  XIcon,
  ZapIcon,
} from 'lucide-vue-next';
import CVButton from '@/components/buttons/CVButton.vue';
import PinchZoomImage from '@/components/PinchZoomImage.vue';
import Textarea from '@/components/Textarea.vue';
import type { FuelType } from '@/entities/cars/fuel-type.enum';
import {
  captureSharpestVideoFrame,
  getCurrentOrientationAngle,
  PHOTO_STREAM_SIZE,
} from '@/utils/camera-capture';
import { useCameraStream } from '@/hooks/use-camera-stream';
import { SHARPNESS_THRESHOLD } from '@/utils/sharpness';
import InspectionShotLocator from './InspectionShotLocator.vue';
import InspectionShotOverlay from './InspectionShotOverlay.vue';
import { MIN_SKIP_REASON_LENGTH, type InspectionShotState } from './inspection-state';
import { useShotLabel } from './use-shot-label';

type CameraShot = { key: InspectionShotKey; state: InspectionShotState; imageUrl?: string };

const props = defineProps<{
  /** `imageUrl` is the photo already taken for the shot; it is shown instead of the camera. */
  shots: CameraShot[];
  initialKey?: InspectionShotKey;
  fuelType?: FuelType | null;
  captureShot: (shotKey: InspectionShotKey, file: File) => Promise<void>;
  skipShot: (shotKey: InspectionShotKey, reason: string) => Promise<void>;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'finished'): void;
}>();

const { t } = useI18n();
const { getShotLabel, getShotHint } = useShotLabel();
const isLandscape = useMediaQuery('(orientation: landscape)');

const viewportRef = ref<HTMLElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
const frozenUrl = ref('');
const frozenFile = ref<File | null>(null);
const frozenSharpness = ref(Infinity);
const imageScale = ref(1);
const isFrozenBlurred = computed(
  () => !!frozenUrl.value && frozenSharpness.value < SHARPNESS_THRESHOLD,
);
const isBusy = ref(false);
const errorMessage = ref('');
const isSkipOpen = ref(false);
const skipReason = ref('');
const videoAspect = ref(16 / 9);
const isCameraUnavailable = ref(false);
const {
  hasTorch,
  isTorchOn,
  toggleTorch: handleToggleTorch,
} = useCameraStream(
  videoRef,
  { video: { facingMode: { ideal: 'environment' }, ...PHOTO_STREAM_SIZE } },
  () => {
    isCameraUnavailable.value = true;
  },
);

const SLIDE_GAP_PX = 16;
const SLIDE_MS = 300;
const SWIPE_SLOP_PX = 10;
const SWIPE_DISTANCE_RATIO = 0.2;
const SWIPE_VELOCITY_PX_MS = 0.4;
/** Zoom factor at which the overlay has faded out: it only guides the unzoomed frame. */
const OVERLAY_FADE_SCALE = 1.3;
const MAIN_BUTTON_LABELS = { shutter: 'takePhoto', confirm: 'confirm', retake: 'retake' } as const;
/** Share of the photo's travel the title moves. */
const TITLE_PARALLAX = 0.3;
/** Titles fade out within the first two thirds of a slide, so they never overlap legibly. */
const TITLE_FADE_SPEED = 1.5;

const findFirstOpenKey = () => props.shots.find((shot) => shot.state === 'open')?.key;
const currentKey = ref(props.initialKey ?? findFirstOpenKey() ?? props.shots[0]?.key);
const currentIndex = computed(() => props.shots.findIndex((shot) => shot.key === currentKey.value));
const currentShot = computed(() => props.shots[currentIndex.value]);
const trimmedReasonLength = computed(() => skipReason.value.trim().length);
/** Whether the current shot shows its existing photo; "retake" switches to the camera. */
const isRetaking = ref(false);
const isReviewing = computed(
  () => !!currentShot.value?.imageUrl && !isRetaking.value && !frozenUrl.value,
);
const displayedImageUrl = computed(
  () => frozenUrl.value || (isReviewing.value ? currentShot.value?.imageUrl : '') || '',
);
const overlayOpacity = computed(() =>
  Math.max(0, 1 - (imageScale.value - 1) / (OVERLAY_FADE_SCALE - 1)),
);
const shotStatusText = computed(() => {
  if (isReviewing.value) return t('reviewCaptured');
  if (currentShot.value?.state === 'captured') return t('alreadyCaptured');
  if (currentShot.value?.state === 'skipped') return t('alreadySkipped');
  return '';
});

const { width: viewportWidth, height: viewportHeight } = useElementSize(viewportRef);
const stageSize = computed(() => {
  const maxWidth = viewportWidth.value - 16;
  const maxHeight = viewportHeight.value - 16;
  if (maxWidth <= 0 || maxHeight <= 0) return { width: 0, height: 0 };
  // Upright, photos can only be viewed: the frame takes the whole area so zooming in can fill it.
  if (!isLandscape.value) return { width: maxWidth, height: maxHeight };
  const width = Math.min(maxWidth, maxHeight * videoAspect.value);
  return { width, height: width / videoAspect.value };
});

const handleVideoMetadata = () => {
  const video = videoRef.value;
  if (!video?.videoWidth || !video.videoHeight) return;
  const ratio = video.videoWidth / video.videoHeight;
  videoAspect.value = ratio >= 1 ? ratio : 1 / ratio;
};

const clearFrozen = () => {
  frozenFile.value = null;
  frozenSharpness.value = Infinity;
  if (frozenUrl.value) URL.revokeObjectURL(frozenUrl.value);
  frozenUrl.value = '';
};

/** Moves to the next shot that is still open, after the current one; finishes when none is left. */
const advance = (justDoneKey: string) => {
  clearFrozen();
  errorMessage.value = '';
  const start = props.shots.findIndex((shot) => shot.key === justDoneKey);
  const ordered = [...props.shots.slice(start + 1), ...props.shots.slice(0, start + 1)];
  const next = ordered.find((shot) => shot.key !== justDoneKey && shot.state === 'open');
  if (!next) {
    emit('finished');
    return;
  }
  currentKey.value = next.key;
};

const handleShutter = async () => {
  if (!videoRef.value || !isLandscape.value) return;
  const frame = await captureSharpestVideoFrame(videoRef.value, getCurrentOrientationAngle());
  if (!frame) return;
  clearFrozen();
  frozenFile.value = frame.file;
  frozenSharpness.value = frame.sharpness;
  frozenUrl.value = URL.createObjectURL(frame.file);
};

/** The big button: take a photo, confirm the one just taken, or switch an existing photo to the camera. */
const mainButtonMode = computed(() => {
  if (frozenUrl.value) return 'confirm';
  return isReviewing.value ? 'retake' : 'shutter';
});
const isMainButtonDisabled = computed(() => {
  if (mainButtonMode.value === 'retake') return false;
  if (mainButtonMode.value === 'confirm') return isBusy.value;
  return !isLandscape.value || isBusy.value || isCameraUnavailable.value;
});

const handleMainButton = () => {
  if (mainButtonMode.value === 'confirm') void handleConfirm();
  else if (mainButtonMode.value === 'retake') isRetaking.value = true;
  else void handleShutter();
};

const handleRetake = () => {
  clearFrozen();
  errorMessage.value = '';
};

const handleConfirm = async () => {
  const key = currentKey.value;
  if (!frozenFile.value || !key || isBusy.value) return;
  isBusy.value = true;
  errorMessage.value = '';
  try {
    await props.captureShot(key, frozenFile.value);
    advance(key);
  } catch {
    errorMessage.value = t('saveFailed');
  } finally {
    isBusy.value = false;
  }
};

const handleOpenSkip = () => {
  skipReason.value = '';
  isSkipOpen.value = true;
};

const handleSkip = async () => {
  const key = currentKey.value;
  if (!key || trimmedReasonLength.value < MIN_SKIP_REASON_LENGTH || isBusy.value) return;
  isBusy.value = true;
  errorMessage.value = '';
  try {
    await props.skipShot(key, skipReason.value.trim());
    isSkipOpen.value = false;
    advance(key);
  } catch {
    errorMessage.value = t('saveFailed');
    isSkipOpen.value = false;
  } finally {
    isBusy.value = false;
  }
};

/** The current shot and its neighbours on the gallery track; the current one shows the live camera when it has no photo. */
const slides = computed(() =>
  [-1, 0, 1].flatMap((offset) => {
    const shot = props.shots[currentIndex.value + offset];
    if (!shot) return [];
    const isCurrent = offset === 0;
    return [
      {
        key: shot.key,
        offset,
        isCurrent,
        imageUrl: isCurrent ? displayedImageUrl.value : shot.imageUrl,
      },
    ];
  }),
);

/** Only the current photo's zoom drives the overlay fade; neighbours stay unzoomed. */
const handleImageScale = (isCurrent: boolean, scale: number) => {
  if (isCurrent) imageScale.value = scale;
};

const swipeX = ref(0);
const isDragging = ref(false);
/** Set for one frame after a slide lands, so the track snaps back to centre without animating. */
const isSnapping = ref(false);
const isSliding = ref(false);
const touchingPointers = new Set<number>();
let swipe: {
  pointerId: number;
  x: number;
  y: number;
  time: number;
  isHorizontal?: boolean;
} | null = null;

const isSlideAnimated = computed(() => !isDragging.value && !isSnapping.value);
const slideTransitionClass = computed(() =>
  isSlideAnimated.value ? 'transition-transform duration-300 ease-out' : '',
);
/** How far the track has moved towards a neighbour: -1 = previous shot in place, 1 = next shot. */
const swipeProgress = computed(() =>
  stageSize.value.width ? -swipeX.value / (stageSize.value.width + SLIDE_GAP_PX) : 0,
);

/** The active progress segment travels along with the photos. */
const indicatorStyle = computed(() => {
  const count = props.shots.length;
  const position = Math.min(count - 1, Math.max(0, currentIndex.value + swipeProgress.value));
  return {
    left: `calc((100% + 4px) * ${position} / ${count})`,
    width: `calc((100% + 4px) / ${count} - 4px)`,
  };
});

const getTitleText = (shot: CameraShot, isCurrent: boolean) => {
  if (isCurrent) return shotStatusText.value || getShotHint(shot.key);
  if (shot.imageUrl) return t('reviewCaptured');
  if (shot.state === 'captured') return t('alreadyCaptured');
  if (shot.state === 'skipped') return t('alreadySkipped');
  return getShotHint(shot.key);
};

/**
 * Titles of the current shot and its neighbours, cross-faded by the swipe and moving a fraction
 * of the photo's way (parallax).
 */
const titles = computed(() =>
  [-1, 0, 1].flatMap((offset) => {
    const shot = props.shots[currentIndex.value + offset];
    if (!shot) return [];
    const distance = offset - swipeProgress.value;
    return [
      {
        key: shot.key,
        index: currentIndex.value + offset,
        text: getTitleText(shot, offset === 0),
        style: {
          '--title-shift': `${distance * stageSize.value.width * TITLE_PARALLAX}px`,
          '--title-opacity': Math.max(0, 1 - Math.abs(distance) * TITLE_FADE_SPEED),
        },
      },
    ];
  }),
);
const getSlideStyle = (offset: number) => ({
  transform: `translateX(${offset * (stageSize.value.width + SLIDE_GAP_PX) + swipeX.value}px)`,
});

/** Swiping pages through the shots like a photo gallery, unless a photo awaits confirmation or is zoomed. */
const canSwipe = () =>
  !frozenUrl.value && !isBusy.value && !isSliding.value && imageScale.value <= 1.01;

const cancelSwipe = () => {
  swipe = null;
  isDragging.value = false;
  swipeX.value = 0;
};

const handleSwipeStart = (event: PointerEvent) => {
  // A primary pointer starts a fresh gesture; drop any pointer whose release went unseen.
  if (event.isPrimary) touchingPointers.clear();
  touchingPointers.add(event.pointerId);
  // A second finger means pinch-to-zoom, which the image handles.
  if (touchingPointers.size > 1) {
    cancelSwipe();
    return;
  }
  if (!canSwipe()) return;
  swipe = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, time: event.timeStamp };
};

const handleSwipeMove = (event: PointerEvent) => {
  if (!swipe || event.pointerId !== swipe.pointerId) return;
  const deltaX = event.clientX - swipe.x;
  const deltaY = event.clientY - swipe.y;
  if (swipe.isHorizontal === undefined) {
    if (Math.hypot(deltaX, deltaY) < SWIPE_SLOP_PX) return;
    swipe.isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
  }
  if (!swipe.isHorizontal) return;
  isDragging.value = true;
  const hasTarget = !!props.shots[currentIndex.value + (deltaX < 0 ? 1 : -1)];
  swipeX.value = hasTarget ? deltaX : deltaX / 3;
};

const handleSwipeEnd = (event: PointerEvent) => {
  touchingPointers.delete(event.pointerId);
  if (!swipe || event.pointerId !== swipe.pointerId) return;
  const distance = swipeX.value;
  const velocity = Math.abs(distance) / Math.max(1, event.timeStamp - swipe.time);
  swipe = null;
  isDragging.value = false;
  const direction = distance < 0 ? 1 : -1;
  const target = props.shots[currentIndex.value + direction];
  const isFarEnough =
    Math.abs(distance) > stageSize.value.width * SWIPE_DISTANCE_RATIO ||
    velocity > SWIPE_VELOCITY_PX_MS;
  if (!target || !isFarEnough) {
    swipeX.value = 0;
    return;
  }
  isSliding.value = true;
  swipeX.value = -direction * (stageSize.value.width + SLIDE_GAP_PX);
  window.setTimeout(() => {
    isSnapping.value = true;
    swipeX.value = 0;
    handleJump(target.key);
    isSliding.value = false;
    requestAnimationFrame(() => requestAnimationFrame(() => (isSnapping.value = false)));
  }, SLIDE_MS);
};

// On the window, so a swipe that ends outside the stage (mouse) still finishes.
useEventListener(window, 'pointermove', handleSwipeMove);
useEventListener(window, 'pointerup', handleSwipeEnd);
useEventListener(window, 'pointercancel', handleSwipeEnd);

const handleJump = (shotKey: InspectionShotKey) => {
  if (isBusy.value) return;
  if (!isSliding.value) cancelSwipe();
  clearFrozen();
  errorMessage.value = '';
  currentKey.value = shotKey;
};

/** Which way titles slide on a jump past the neighbours, so they follow the move through the shots. */
const titleDirection = ref<'forward' | 'back'>('forward');

watch(currentKey, (nextKey, previousKey) => {
  isRetaking.value = false;
  const getIndex = (key?: InspectionShotKey) => props.shots.findIndex((shot) => shot.key === key);
  titleDirection.value = getIndex(nextKey) < getIndex(previousKey) ? 'back' : 'forward';
});
watch(displayedImageUrl, () => {
  imageScale.value = 1;
});

const handleClose = () => {
  emit('close');
};

onUnmounted(() => {
  clearFrozen();
});
</script>

<style scoped>
.hud-title {
  transform: translateX(var(--title-shift));
  opacity: var(--title-opacity);
}
/* Jumps further than one shot: the titles that come and go slide in the direction of the jump. */
.hud-title.hud-title-forward-enter-from,
.hud-title.hud-title-back-leave-to {
  opacity: 0;
  transform: translateX(var(--title-travel));
}
.hud-title.hud-title-forward-leave-to,
.hud-title.hud-title-back-enter-from {
  opacity: 0;
  transform: translateX(calc(-1 * var(--title-travel)));
}
.rotate-hint {
  animation: rotate-hint 2.2s ease-in-out infinite;
}
@keyframes rotate-hint {
  0%,
  20% {
    transform: rotate(0deg);
  }
  50%,
  80% {
    transform: rotate(-90deg);
  }
  100% {
    transform: rotate(0deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .rotate-hint {
    animation: none;
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "takePhoto": "Take photo",
    "confirm": "Confirm",
    "retake": "Retake",
    "flash": "Flash",
    "close": "Close",
    "cancel": "Cancel",
    "skip": "Skip",
    "skipTitle": "Skip “{shot}”",
    "skipText": "Explain why this photo cannot be taken. The reason is stored with the inspection.",
    "skipPlaceholder": "e.g. Boot fully loaded, customer luggage inside",
    "skipHint": "{count} / at least {min} characters",
    "skipConfirm": "Skip photo",
    "rotateTitle": "Hold the device in landscape",
    "rotateText": "All vehicle photos are taken in landscape so they line up for comparison.",
    "alreadyCaptured": "Captured · a new photo replaces it",
    "reviewCaptured": "Captured · swipe to browse, retake to replace",
    "alreadySkipped": "Skipped · a photo replaces the skip",
    "saveFailed": "Could not save. Check the connection and try again.",
    "blurHint": "Photo looks blurry. Zoom in to check or take it again.",
    "cameraUnavailableTitle": "Camera not available",
    "cameraUnavailableText": "Allow camera access in the browser or app settings. If the camera cannot be used, skip each photo with a reason."
  },
  "de": {
    "takePhoto": "Foto aufnehmen",
    "confirm": "Bestätigen",
    "retake": "Erneut",
    "flash": "Blitz",
    "close": "Schließen",
    "cancel": "Abbrechen",
    "skip": "Überspringen",
    "skipTitle": "„{shot}“ überspringen",
    "skipText": "Begründe, warum dieses Foto nicht möglich ist. Die Begründung wird mit den Fahrzeugfotos gespeichert.",
    "skipPlaceholder": "z. B. Kofferraum voll beladen, Gepäck des Kunden",
    "skipHint": "{count} / mindestens {min} Zeichen",
    "skipConfirm": "Foto überspringen",
    "rotateTitle": "Gerät quer halten",
    "rotateText": "Alle Fahrzeugfotos werden im Querformat aufgenommen, damit sie sich vergleichen lassen.",
    "alreadyCaptured": "Aufgenommen · neues Foto ersetzt es",
    "reviewCaptured": "Aufgenommen · wischen zum Blättern, neu aufnehmen zum Ersetzen",
    "alreadySkipped": "Übersprungen · ein Foto ersetzt das Überspringen",
    "saveFailed": "Speichern fehlgeschlagen. Verbindung prüfen und erneut versuchen.",
    "blurHint": "Foto wirkt unscharf. Zum Prüfen heranzoomen oder neu aufnehmen.",
    "cameraUnavailableTitle": "Kamera nicht verfügbar",
    "cameraUnavailableText": "Kamerazugriff in den Browser- oder App-Einstellungen erlauben. Geht die Kamera nicht, jedes Foto mit Begründung überspringen."
  },
  "es": {
    "takePhoto": "Hacer foto",
    "confirm": "Confirmar",
    "retake": "Repetir",
    "flash": "Flash",
    "close": "Cerrar",
    "cancel": "Cancelar",
    "skip": "Omitir",
    "skipTitle": "Omitir «{shot}»",
    "skipText": "Explica por qué no se puede hacer esta foto. El motivo se guarda con la inspección.",
    "skipPlaceholder": "p. ej. maletero lleno con el equipaje del cliente",
    "skipHint": "{count} / al menos {min} caracteres",
    "skipConfirm": "Omitir foto",
    "rotateTitle": "Sujeta el dispositivo en horizontal",
    "rotateText": "Todas las fotos del vehículo se hacen en horizontal para poder compararlas.",
    "alreadyCaptured": "Hecha · una foto nueva la sustituye",
    "reviewCaptured": "Hecha · desliza para ver las demás, repite para sustituirla",
    "alreadySkipped": "Omitida · una foto sustituye la omisión",
    "saveFailed": "No se pudo guardar. Comprueba la conexión e inténtalo de nuevo.",
    "blurHint": "La foto parece borrosa. Amplía para comprobarla o repítela.",
    "cameraUnavailableTitle": "Cámara no disponible",
    "cameraUnavailableText": "Permite el acceso a la cámara en los ajustes del navegador o de la app. Si la cámara no funciona, omite cada foto con un motivo."
  }
}
</i18n>
